import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { HardHat, TriangleAlert } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("login");
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/public/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);
  const [loading, setLoading] = useState(false);

  // Login state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // Register state
  const [rName, setRName] = useState("");
  const [rEmail, setREmail] = useState("");
  const [rPassword, setRPassword] = useState("");
  const [rRole, setRRole] = useState("solicitante");

  // Forgot password
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);

  const onLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) toast.error(res.error);
    else { toast.success("Bem-vindo!"); navigate("/", { replace: true }); }
  };

  const requestReset = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) return toast.error("Informe o email");
    setForgotLoading(true);
    try {
      await api.post("/auth/password-reset-request", { email: forgotEmail });
      toast.success("Se o email estiver cadastrado, você receberá o link em instantes.");
      setForgotOpen(false);
      setForgotEmail("");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setForgotLoading(false); }
  };

  const onRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await register({ name: rName, email: rEmail, password: rPassword, role: rRole });
    setLoading(false);
    if (!res.ok) toast.error(res.error);
    else { toast.success("Conta criada!"); navigate("/", { replace: true }); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left: form */}
      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-11 h-11 rounded-sm bg-orange-500 flex items-center justify-center">
              <HardHat className="w-6 h-6 text-white" />
            </div>
            <div>
              <div className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Sistema PCM</div>
              <div className="font-heading font-black text-xl leading-tight">Britagem & Moagem</div>
            </div>
          </div>

          <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tight mb-2">
            Planejamento &<br />Controle de<br />Manutenção
          </h1>
          <p className="text-muted-foreground mb-8">
            Gestão de Ordens de Serviço para plantas de mineração e beneficiamento.
          </p>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid w-full grid-cols-2 mb-6" data-testid="auth-tabs">
              <TabsTrigger value="login" data-testid="tab-login">Entrar</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register">Cadastrar</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <Card className="p-6 shadow-none rounded-sm">
                <form onSubmit={onLogin} className="space-y-4">
                  <div>
                    <Label htmlFor="l-email">Email</Label>
                    <Input id="l-email" data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="rounded-sm mt-1.5" />
                  </div>
                  <div>
                    <Label htmlFor="l-pass">Senha</Label>
                    <Input id="l-pass" data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="rounded-sm mt-1.5" />
                  </div>
                  <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full rounded-sm bg-orange-500 hover:bg-orange-600 text-white font-semibold">
                    {loading ? "Entrando..." : "Entrar no Sistema"}
                  </Button>
                  <button
                    type="button"
                    onClick={() => { setForgotEmail(email); setForgotOpen(true); }}
                    className="text-xs text-orange-600 hover:underline mt-2 w-full text-center"
                    data-testid="forgot-password-link"
                  >
                    Esqueci minha senha
                  </button>
                </form>
              </Card>
            </TabsContent>

            <TabsContent value="register">
              <Card className="p-6 shadow-none rounded-sm">
                <form onSubmit={onRegister} className="space-y-4">
                  <div>
                    <Label htmlFor="r-name">Nome completo</Label>
                    <Input id="r-name" data-testid="register-name" value={rName} onChange={(e) => setRName(e.target.value)} required className="rounded-sm mt-1.5" />
                  </div>
                  <div>
                    <Label htmlFor="r-email">Email</Label>
                    <Input id="r-email" data-testid="register-email" type="email" value={rEmail} onChange={(e) => setREmail(e.target.value)} required className="rounded-sm mt-1.5" />
                  </div>
                  <div>
                    <Label htmlFor="r-pass">Senha</Label>
                    <Input id="r-pass" data-testid="register-password" type="password" value={rPassword} onChange={(e) => setRPassword(e.target.value)} required minLength={6} className="rounded-sm mt-1.5" />
                  </div>
                  <div>
                    <Label>Função</Label>
                    <Select value={rRole} onValueChange={setRRole}>
                      <SelectTrigger data-testid="register-role" className="rounded-sm mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="solicitante">Solicitante</SelectItem>
                        <SelectItem value="tecnico">Técnico</SelectItem>
                        <SelectItem value="almoxarife">Almoxarife</SelectItem>
                        <SelectItem value="supervisor">Supervisor</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button type="submit" disabled={loading} data-testid="register-submit" className="w-full rounded-sm bg-orange-500 hover:bg-orange-600 text-white font-semibold">
                    {loading ? "Criando..." : "Criar Conta"}
                  </Button>
                </form>
              </Card>
            </TabsContent>
          </Tabs>

          <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
            <DialogContent data-testid="forgot-dialog">
              <DialogHeader><DialogTitle>Recuperar senha</DialogTitle></DialogHeader>
              <form onSubmit={requestReset} className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Informe o email cadastrado. Enviaremos um link de redefinição válido por 2 horas. O supervisor também é notificado.
                </p>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    required
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    className="rounded-sm mt-1.5"
                    data-testid="forgot-email"
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setForgotOpen(false)} className="rounded-sm">Cancelar</Button>
                  <Button type="submit" disabled={forgotLoading} className="rounded-sm bg-orange-500 hover:bg-orange-600 text-white" data-testid="forgot-submit">
                    {forgotLoading ? "Enviando..." : "Enviar Link"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Right: image */}
      <div className="hidden lg:block relative bg-slate-950 overflow-hidden">
        <img
          src="https://customer-assets-4nw71qhi.emergentagent.net/job_crusher-mill-manager/artifacts/is4l6qom_PLANTA%20INDUSCALTA.jpeg"
          alt="Planta Induscalta Calcários"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-slate-950 via-slate-950/50 to-slate-950/10" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="flex items-center gap-2 mb-3">
            <TriangleAlert className="w-5 h-5 text-orange-400" />
            <span className="text-xs tracking-[0.2em] uppercase font-semibold text-orange-400">Induscalta Calcários</span>
          </div>
          <h2 className="font-heading text-3xl font-black mb-3 leading-tight">
            Zero paradas não planejadas.<br />
            Máxima disponibilidade operacional.
          </h2>
          <p className="text-slate-300 text-sm max-w-md">
            Controle total sobre ordens de serviço, KPIs (MTBF/MTTR), histórico de manutenção e recomendações técnicas com IA.
          </p>
          <div className="mt-6 flex gap-6 text-xs">
            <div><div className="text-orange-400 font-heading font-black text-2xl" data-testid="login-stat-equipments">{stats?.equipment_count ?? "—"}</div><div className="text-slate-400 uppercase tracking-wider">Equipamentos</div></div>
            <div><div className="text-orange-400 font-heading font-black text-2xl" data-testid="login-stat-sectors">{stats?.sector_count ?? "—"}</div><div className="text-slate-400 uppercase tracking-wider">Setores</div></div>
            <div><div className="text-orange-400 font-heading font-black text-2xl" data-testid="login-stat-disciplines">{stats?.discipline_count ?? "—"}</div><div className="text-slate-400 uppercase tracking-wider">Disciplinas</div></div>
          </div>
        </div>
      </div>
    </div>
  );
}
